import React, { useState, ChangeEvent } from 'react';
import axios from 'axios';
import { X, Edit } from 'lucide-react';
import toast from 'react-hot-toast';
import PhotoUploadGrid from '../../components/PhotoUploadGrid';
import { useOverlayClose } from '../../components/useOverlayClose';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const inp: React.CSSProperties = {
  width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #cbd5e1',
  borderRadius: '6px', fontSize: '0.82rem', boxSizing: 'border-box',
  height: '34px', outline: 'none',
};
const lbl: React.CSSProperties = {
  display: 'block', fontSize: '0.68rem', color: '#64748b', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem',
};
const col: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.2rem' };

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BatchForEdit {
  id: number;
  batch_no: string | null;
  vendor_name: string | null;
  hsn_sac: string | null;
  purchase_inv_no: string | null;
  purchase_inv_date: string | null;
  expiry_date: string | null;
  mrp: number | null;
  dealer_price: number | null;
  selling_price: number | null;
  photo_1_url: string | null;
  photo_2_url: string | null;
  photo_3_url: string | null;
  photo_4_url: string | null;
  photo_5_url: string | null;
  primary_photo_slot: number | null;
}

interface Props {
  batch: BatchForEdit;
  onClose: () => void;
  onSuccess: () => void;
}

interface EditForm {
  batch_no: string;
  vendor_name: string;
  hsn_sac: string;
  purchase_inv_no: string;
  purchase_inv_date: string;
  expiry_date: string;
  mrp: string;
  dealer_price: string;
  selling_price: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

const BatchEditModal: React.FC<Props> = ({ batch, onClose, onSuccess }) => {
  const [form, setForm] = useState<EditForm>({
    batch_no:          batch.batch_no          ?? '',
    vendor_name:       batch.vendor_name       ?? '',
    hsn_sac:           batch.hsn_sac           ?? '',
    purchase_inv_no:   batch.purchase_inv_no   ?? '',
    purchase_inv_date: batch.purchase_inv_date ? batch.purchase_inv_date.split('T')[0] : '',
    expiry_date:       batch.expiry_date       ? batch.expiry_date.split('T')[0]       : '',
    mrp:               batch.mrp          != null ? String(batch.mrp)          : '',
    dealer_price:      batch.dealer_price  != null ? String(batch.dealer_price)  : '',
    selling_price:     batch.selling_price != null ? String(batch.selling_price) : '',
  });

  // New file selections (null = no new file chosen; existing URL is kept as-is)
  const [photos, setPhotos] = useState<(File | null)[]>([null, null, null, null, null]);
  const existingUrls = [batch.photo_1_url, batch.photo_2_url, batch.photo_3_url, batch.photo_4_url, batch.photo_5_url];
  const [primarySlot, setPrimarySlot] = useState<number | null>(batch.primary_photo_slot ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlayClose = useOverlayClose(onClose);

  const handle = (e: ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

    const batchJson = JSON.stringify({
      batch_no:          form.batch_no.trim()        || null,
      vendor_name:       form.vendor_name.trim()     || null,
      hsn_sac:           form.hsn_sac.trim()         || null,
      purchase_inv_no:   form.purchase_inv_no.trim() || null,
      purchase_inv_date: form.purchase_inv_date      || null,
      expiry_date:       form.expiry_date            || null,
      mrp:               form.mrp          ? parseFloat(form.mrp)          : null,
      dealer_price:      form.dealer_price  ? parseFloat(form.dealer_price)  : null,
      selling_price:     form.selling_price ? parseFloat(form.selling_price) : null,
      primary_photo_slot: primarySlot,
    });

    const formData = new FormData();
    formData.append('batch_data', batchJson);
    photos.forEach((photo, i) => { if (photo) formData.append(`photo_${i + 1}`, photo); });

    setIsSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/consumables/batches/${batch.id}`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Batch updated.');
      onSuccess();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update batch.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      {...overlayClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ background: '#eff6ff', borderRadius: '0.5rem', padding: '0.4rem', display: 'flex' }}>
              <Edit size={17} color="#2563eb" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>Edit Batch Metadata</h2>
              <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                {batch.batch_no ? `Batch: ${batch.batch_no}` : `Batch ID: ${batch.id}`} — quantities are ledger-protected
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X size={18} />
          </button>
        </div>

        {/* ── Form ── */}
        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.9rem', overflowY: 'auto', maxHeight: '65vh' }}>

          {/* Row 1: Batch No | Vendor | HSN/SAC */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem' }}>
            <div style={col}>
              <label style={lbl}>Batch Number</label>
              <input name="batch_no" value={form.batch_no} onChange={handle} placeholder="e.g. BT-2024-001" style={inp} autoFocus />
            </div>
            <div style={col}>
              <label style={lbl}>Vendor / Dealer</label>
              <input name="vendor_name" value={form.vendor_name} onChange={handle} placeholder="e.g. MedSupply Co." style={inp} />
            </div>
            <div style={col}>
              <label style={lbl}>HSN / SAC Code</label>
              <input name="hsn_sac" value={form.hsn_sac} onChange={handle} placeholder="e.g. 30049099" style={inp} />
            </div>
          </div>

          {/* Row 2: Invoice No | Purchase Date | Expiry Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem' }}>
            <div style={col}>
              <label style={lbl}>Invoice No.</label>
              <input name="purchase_inv_no" value={form.purchase_inv_no} onChange={handle} placeholder="e.g. INV-5467" style={inp} />
            </div>
            <div style={col}>
              <label style={lbl}>Purchase Date</label>
              <input name="purchase_inv_date" type="date" value={form.purchase_inv_date} onChange={handle} style={inp} />
            </div>
            <div style={col}>
              <label style={lbl}>Expiry Date</label>
              <input name="expiry_date" type="date" value={form.expiry_date} onChange={handle} style={inp} />
            </div>
          </div>

          {/* Row 3: MRP | Dealer Price | Selling Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem' }}>
            <div style={col}>
              <label style={lbl}>MRP (₹)</label>
              <input name="mrp" type="number" min="0" step="0.01" value={form.mrp} onChange={handle} placeholder="0.00" style={inp} />
            </div>
            <div style={col}>
              <label style={lbl}>Dealer Price (₹)</label>
              <input name="dealer_price" type="number" min="0" step="0.01" value={form.dealer_price} onChange={handle} placeholder="0.00" style={inp} />
            </div>
            <div style={col}>
              <label style={lbl}>Selling Price (₹)</label>
              <input name="selling_price" type="number" min="0" step="0.01" value={form.selling_price} onChange={handle} placeholder="0.00" style={inp} />
            </div>
          </div>

          {/* Row 4: Batch Photos */}
          <div>
            <p style={{ ...lbl, marginBottom: '0.5rem' }}>
              Batch Photos{' '}
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}>(optional — click a photo to replace, ★ to set the display photo)</span>
            </p>
            <PhotoUploadGrid
              files={photos}
              existingUrls={existingUrls}
              onChange={setPhotos}
              primarySlot={primarySlot}
              onPrimaryChange={setPrimarySlot}
            />
          </div>

        </div>

        {/* ── Footer ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', padding: '0.9rem 1.5rem', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.1rem', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ padding: '0.5rem 1.4rem', backgroundColor: isSubmitting ? '#94a3b8' : '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 700 }}
          >
            {isSubmitting ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchEditModal;
